import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { messageLogSelectByQuery, userMessagesSelectOneObjectByQuery } from '@common-reducers/DBServiceThunks';
import { MessageLogStatusBadge } from './MessageLogStatusBadge';
import { AlertCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useMemo } from 'react';
import { deriveMessageBadgeStatus, messageHasDeliveryEvidence } from '@common-reducers/CampaignSelectors';
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { isNotOnWhatsAppFailure } from '@common-components/message-status/message-status-config';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';

interface MessageStatusWithErrorProps {
    messageId: string;
    className?: string;
}

/**
 * Displays a message's status badge + error indication, selecting all of the
 * message's logs from Redux. The two-axis status is resolved by the shared
 * deriveMessageBadgeStatus (rep-pick via statusRank + cross-row receipt
 * aggregation + overdue→expired fold), so the per-message badge agrees with the
 * aggregate stats on the representative row and renders `confirmed` first-class
 * ("Confirmed", not "Sent").
 */
export function MessageStatusWithError({ messageId, className }: MessageStatusWithErrorProps) {
    const { t } = useTranslation(namespace);
    // Get all message logs for this messageId from Redux state
    const messageLogs = useAppSelector((state) =>
        messageId ? messageLogSelectByQuery(state.DBMessageLogReducer, {
            messageId
        }) : []
    );

    // Get the UserMessage to check if it's expired - try from DBUserMessageReducer first
    const userMessageFromReducer = useAppSelector((state) =>
        messageId ? userMessagesSelectOneObjectByQuery(state.DBUserMessageReducer, {
            _id: messageId
        }) : null
    );

    // BE#658 (4a): dedicated delivery-evidence doc (scoped fetch for the campaign log view). The
    // real affected shape is a before-wa-send-only trail whose embedded snapshot is pre-send, so the
    // confirmed userMessage proving delivery is NOT in the log — it comes from this map.
    const deliveryEvidence = useAppSelector((state) =>
        messageId ? state.CampaignReducer.deliveryEvidence?.[messageId] : undefined
    );

    // Delivery-evidence source order: full store userMessage → the dedicated evidence map → the
    // log's embedded (pre-send) snapshot.
    const userMessage = useMemo(() => {
        if (userMessageFromReducer) return userMessageFromReducer;
        if (deliveryEvidence) return deliveryEvidence as unknown as UserMessage;

        // Try to get message from the first message log (for campaigns)
        if (messageLogs && messageLogs.length > 0) {
            const embeddedMessage = messageLogs[0]?.message as UserMessage | undefined;
            if (embeddedMessage && embeddedMessage.dueDate) {
                return embeddedMessage;
            }
        }
        return null;
    }, [userMessageFromReducer, deliveryEvidence, messageLogs]);

    // Compute hasError from logs. BE#658: a DELIVERED userMessage (delivery evidence) must not
    // show the red error icon over a stray losing-engine/timeout `failed` log — same override as
    // the badge (deriveMessageBadgeStatus). A genuine failure (no delivery evidence) still shows it.
    const hasError = useMemo(() => {
        if (!messageLogs || messageLogs.length === 0) {
            return false;
        }
        if (messageHasDeliveryEvidence(userMessage)) return false;
        // Check if any log has an error
        return messageLogs.some(log => log.status === 'failed' || log.error);
    }, [messageLogs, userMessage]);

    // Group vs 1:1 — the WA JID lives on the embedded message's contactList
    // (same source the two-axis data layer resolves chatId from), falling back
    // to the log's contactWAId.
    const isGroup = useMemo(() => {
        const chatId = userMessage?.contactList?.[0]?.id
            ?? messageLogs?.[0]?.message?.contactList?.[0]?.id
            ?? messageLogs?.[0]?.contactWAId;
        return chatKindOf(chatId) === 'group';
    }, [userMessage, messageLogs]);

    // Canonical two-axis resolution: representative row (statusRank) + cross-row
    // receipt aggregation + overdue→expired fold, shared with the aggregate stats.
    const derived = useMemo(
        () => deriveMessageBadgeStatus(messageLogs ?? [], isGroup, userMessage ?? undefined),
        [messageLogs, isGroup, userMessage],
    );

    if (!derived) {
        return null;
    }

    return (
        <div className={className || "flex items-center gap-2"}>
            <MessageLogStatusBadge derived={derived} />
            {hasError && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div className="cursor-help">
                            <AlertCircle className="h-4 w-4 text-red-600" />
                        </div>
                    </TooltipTrigger>
                    <TooltipContent
                        side="top"
                    >
                        <p className="text-xs">
                            {isNotOnWhatsAppFailure(userMessage)
                                ? t('messageStatus.failedNotOnWhatsApp.description')
                                : t('messageStatus.errorTooltip')}
                        </p>
                    </TooltipContent>
                </Tooltip>
            )}
        </div>
    );
}
